import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";

import useTrendScout from "../../../hooks/useTrendScout";
import useToaster from "../../../hooks/useToaster";
import useLanguage from "../../../hooks/useLanguage";
import useMemorize from "../../../hooks/useMemorize";
import { doneColor, failColor, heatColor, topicColor } from "../topics";
import {
  CardAction,
  CardDesc,
  CardFoot,
  CardGrid,
  CardMeta,
  CardTitle,
  EmptyState,
  HeatBadge,
  MetaDot,
  PaginationBar,
  PaginationControls,
  PaginationInfo,
  PillCount,
  RadarOrb,
  SourceLink,
  StatePill,
  StoryCard,
  StripDivider,
  Toolbar,
  TopicPill,
  TopicStrip,
} from "../style.jsx";
import TrendScoutSkeleton from "../skeleton.jsx";
import { formatRelative } from "../utils.js";
import Flag, { CountryTip } from "../flag.jsx";

const PAGE_SIZE = 24;
// A write takes a few minutes and reports no progress, so the card can only
// learn it finished by asking again.
const POLL_MS = 30_000;
// The feed is shared. Even with nothing of your own running, someone else's
// claim has to reach your screen or you'll click a story they already took.
const IDLE_POLL_MS = 60_000;

const navigate = (to) => {
  window.history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

export default function ArticlesTab({ status, text }) {
  const { listItems, generate } = useTrendScout();
  const { addToaster } = useToaster();
  // The confirm dialog renders in a portal, outside the `.rtl` container that
  // App.js flips — so it needs the direction applied on its own paper.
  const [{ direction }] = useLanguage();
  const mode = useTheme().palette.mode;
  // TrendScout content is Hebrew, so the feed reads RTL by default regardless
  // of the UI language — matching the Highlights feed. Persisted + toggleable.
  const [rtlMemo, setRtlMemo] = useMemorize("trendscout-rtl");
  const isRtl = rtlMemo !== "0";
  const dir = isRtl ? "rtl" : "ltr";

  const [items, setItems] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [countryAnchor, setCountryAnchor] = useState(null);
  // One topic at a time — the strip is the page's spine, not a multi-select.
  // Seeded from ?topic= so the home dashboard can link into a single desk.
  const [topic, setTopic] = useState(
    () => new URLSearchParams(window.location.search).get("topic") || "",
  );
  // ?guid= from a dashboard row: the story the editor actually clicked. It
  // arrives on All, where the feed order matches the dashboard's, so the card
  // is on page 1 by construction.
  const [focusGuid, setFocusGuid] = useState(
    () => new URLSearchParams(window.location.search).get("guid") || "",
  );
  const focusRef = React.useRef(null);
  const [topicCounts, setTopicCounts] = useState({});
  const [writtenCount, setWrittenCount] = useState(0);
  // Independent of the topic pills — you can ask for written Sports stories.
  const [writtenOnly, setWrittenOnly] = useState(false);
  const [sort, setSort] = useMemorize("trendscout-sort");

  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);
  // guid -> click time. Bridges the gap until the server's own claim shows up
  // in the feed; see isWriting for why it has to expire.
  const [queued, setQueued] = useState(() => new Map());
  const [confirmRow, setConfirmRow] = useState(null);

  // Debounce the search box → back to page 1 on every new query.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPageIndex(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchItems = useCallback(
    () =>
      listItems({
        page: pageIndex + 1,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        countries: selectedCountries,
        topics: topic ? [topic] : [],
        sort: sort === "new" ? "new" : "hot",
        status: writtenOnly ? "written" : "",
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageIndex, debouncedSearch, selectedCountries, topic, sort, writtenOnly],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchItems();
        if (!alive) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        if (typeof res?.total === "number") setTotal(res.total);
        setTopicCounts(res?.topicCounts || {});
        setWrittenCount(res?.writtenCount || 0);
        const avCountries = res?.availableCountries || [];
        setAvailableCountries(avCountries);
        // Drop selections the narrowed set no longer offers, so a stale chip
        // can't keep the grid empty with no visible way back.
        setSelectedCountries((prev) => {
          const next = prev.filter((c) => avCountries.includes(c));
          return next.length === prev.length ? prev : next;
        });
      } catch (_) {
        if (alive) setItems([]);
      } finally {
        if (alive) setInitialLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchItems]);

  // While a card on this page is mid-write, keep asking so it flips to "ready"
  // on its own — the editor never has to leave for the queue page.
  //
  // The optimistic flag has to time out. The server answers the claim before it
  // returns, but the feed it serves is cached for 60s, so the claim isn't
  // visible right away. Past that window the server is the only truth: a job
  // that died released its claim, and a card that trusted the click forever
  // would spin until someone reloaded the page.
  const isWriting = (item) => {
    if (item.generatedArticleId) return false;
    if (item.generatingAt) return true;
    const clickedAt = queued.get(item.guid);
    return clickedAt != null && Date.now() - clickedAt < 90_000;
  };
  const anyWriting = items.some(isWriting);
  // Always polling, just faster while something of ours is running. Gating it
  // on anyWriting deadlocked the card: reload mid-write and the optimistic flag
  // is gone, so nothing looked like it was writing, so nothing ever refetched,
  // so the card sat on its old state until the page was reloaded again.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const res = await fetchItems();
        setItems(Array.isArray(res?.items) ? res.items : []);
      } catch (_) {
        /* transient — the next tick retries */
      }
    }, anyWriting ? POLL_MS : IDLE_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyWriting, fetchItems]);

  // The grid scrolls inside the page, so paging while scrolled down would drop
  // the editor into the middle of the next page.
  const gridRef = React.useRef(null);
  useEffect(() => {
    gridRef.current?.scrollTo({ top: 0 });
  }, [pageIndex, topic]);

  // Bring the deep-linked card into view, let its ring play out, then drop the
  // guid — from both state and the URL — so a reload or the generation poll
  // can't make it flash a second time.
  useEffect(() => {
    if (!focusGuid || !initialLoaded) return;
    const el = focusRef.current;
    el?.scrollIntoView({
      block: "center",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
    const t = setTimeout(() => {
      setFocusGuid("");
      window.history.replaceState({}, "", window.location.pathname);
    }, el ? 2400 : 0);
    return () => clearTimeout(t);
  }, [focusGuid, initialLoaded, items]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isFiltering =
    !!debouncedSearch || selectedCountries.length > 0 || !!topic || writtenOnly;

  const pickTopic = (t) => {
    setTopic(t);
    setPageIndex(0);
  };
  const toggleCountry = (value) => {
    setSelectedCountries((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
    setPageIndex(0);
  };

  const handleGenerate = async (row) => {
    const guid = row?.guid;
    // Guard on the card's live state, not on Map membership: an expired entry
    // sticks around, and it must not block a retry after a failed job.
    if (!guid || isWriting(row) || row.generatedArticleId) return;
    const release = () =>
      setQueued((prev) => {
        const next = new Map(prev);
        next.delete(guid);
        return next;
      });
    setQueued((prev) => new Map(prev).set(guid, Date.now()));
    try {
      const res = await generate(guid);
      if (res?.enqueued === true || res?.alreadyQueued) {
        addToaster(
          res.enqueued
            ? { message: text.toaster.generationQueued, severity: "success" }
            : {
                message: text.toaster.generationAlreadyQueued,
                severity: "info",
              },
        );
      } else {
        release();
        addToaster({
          message: res?.message || text.toaster.generationFailed,
          severity: "error",
        });
      }
    } catch (e) {
      release();
      addToaster({
        message: e?.message || text.toaster.generationFailed,
        severity: "error",
      });
    }
  };

  // Strip order comes from the lang file, not the API, so the pills never
  // reshuffle under the cursor. A topic only disappears once it's both empty
  // and unselected.
  const topicPills = Object.keys(text.topicNames).filter(
    (t) => topicCounts[t] > 0 || t === topic,
  );
  // `total` is the selected topic's count, so All has to sum the strip.
  const allCount = Object.values(topicCounts).reduce((a, b) => a + b, 0);

  if (!initialLoaded) return <TrendScoutSkeleton rows={6} cards />;

  return (
    <>
      <TopicStrip>
        <TopicPill
          type="button"
          $active={!topic}
          $bg={topicColor("default", mode).bg}
          $fg={topicColor("default", mode).fg}
          onClick={() => pickTopic("")}
          aria-pressed={!topic}
        >
          {text.filters.all}
          <PillCount>{allCount}</PillCount>
        </TopicPill>
        {topicPills.map((t) => {
          const tc = topicColor(t, mode);
          return (
            <TopicPill
              key={t}
              type="button"
              $active={topic === t}
              $bg={tc.bg}
              $fg={tc.fg}
              onClick={() => pickTopic(topic === t ? "" : t)}
              aria-pressed={topic === t}
            >
              {text.topicNames[t] || t}
              <PillCount>{topicCounts[t] || 0}</PillCount>
            </TopicPill>
          );
        })}

        <StripDivider />
        <Tooltip title={text.filters.writtenTip} placement="top" arrow>
          <TopicPill
            type="button"
            $active={writtenOnly}
            $bg={doneColor(mode).bg}
            $fg={doneColor(mode).fg}
            onClick={() => {
              setWrittenOnly((v) => !v);
              setPageIndex(0);
            }}
            aria-pressed={writtenOnly}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
            {text.filters.written}
            <PillCount>{writtenCount}</PillCount>
          </TopicPill>
        </Tooltip>
      </TopicStrip>

      <Toolbar>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            alignItems: "center",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "minmax(220px, 1fr) 180px auto auto",
            },
            gridTemplateAreas: {
              xs: '"search search" "country sort" "rtl rtl"',
              sm: '"search country sort rtl"',
            },
          }}
        >
          <TextField
            name="search"
            fullWidth
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={text.filters.searchPlaceholder}
            sx={{ gridArea: "search" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FilterButton
            area="country"
            Icon={PublicOutlinedIcon}
            label={text.filters.countries}
            count={selectedCountries.length}
            disabled={availableCountries.length === 0}
            onClick={(e) => setCountryAnchor(e.currentTarget)}
          />
          <CountryMenu
            anchorEl={countryAnchor}
            onClose={() => setCountryAnchor(null)}
            options={availableCountries}
            selected={selectedCountries}
            onToggle={toggleCountry}
            onClear={() => {
              setSelectedCountries([]);
              setPageIndex(0);
              setCountryAnchor(null);
            }}
            emptyLabel={text.filters.countriesEmpty}
            clearLabel={text.filters.clear}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={sort === "new" ? "new" : "hot"}
            onChange={(_, v) => {
              if (!v) return;
              setSort(v);
              setPageIndex(0);
            }}
            sx={{
              gridArea: "sort",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                px: 1.5,
                py: 0.75,
                borderColor: "divider",
              },
            }}
          >
            <ToggleButton value="hot">{text.filters.sortHot}</ToggleButton>
            <ToggleButton value="new">{text.filters.sortNew}</ToggleButton>
          </ToggleButtonGroup>

          <FormControlLabel
            sx={{ gridArea: "rtl", justifySelf: "end", marginInlineEnd: 0 }}
            control={
              <Switch
                size="small"
                checked={isRtl}
                onChange={() => setRtlMemo(isRtl ? "0" : "1")}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {text.filters.rtlToggle}
              </Typography>
            }
          />
        </Box>
      </Toolbar>

      {items.length === 0 ? (
        <EmptyState>
          <RadarOrb>
            <Box
              component="img"
              src="/assets/icons/trendscout.svg"
              alt=""
              sx={{ width: 36, height: 36, opacity: 0.7 }}
            />
          </RadarOrb>
          <Typography variant="h6" fontWeight={700}>
            {isFiltering ? text.table.noMatches : text.table.empty}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isFiltering
              ? text.table.noMatchesHint
              : nextScanLine(status, text)}
          </Typography>
        </EmptyState>
      ) : (
        <CardGrid dir={dir} ref={gridRef}>
          {items.map((item, i) => (
            <StoryCardView
              key={item.guid}
              item={item}
              index={i}
              mode={mode}
              dir={dir}
              text={text}
              writing={isWriting(item)}
              mine={queued.has(item.guid)}
              showTopic={!topic}
              flash={item.guid === focusGuid}
              cardRef={item.guid === focusGuid ? focusRef : undefined}
              onWrite={() => setConfirmRow(item)}
            />
          ))}
        </CardGrid>
      )}

      <PaginationBar>
        <PaginationInfo>
          {text.table.pageOf
            .replace("{n}", pageIndex + 1)
            .replace("{m}", pageCount)}
          {" · "}
          {text.table.totalLabel.replace("{n}", total)}
        </PaginationInfo>
        <PaginationControls>
          <Tooltip title={text.table.prevPage}>
            <span>
              <IconButton
                size="small"
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                disabled={pageIndex === 0}
              >
                {direction === "rtl" ? (
                  <ChevronRightRoundedIcon />
                ) : (
                  <ChevronLeftRoundedIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={text.table.nextPage}>
            <span>
              <IconButton
                size="small"
                onClick={() =>
                  setPageIndex((p) => (p + 1 >= pageCount ? p : p + 1))
                }
                disabled={pageIndex + 1 >= pageCount}
              >
                {direction === "rtl" ? (
                  <ChevronLeftRoundedIcon />
                ) : (
                  <ChevronRightRoundedIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </PaginationControls>
      </PaginationBar>

      <Dialog
        open={!!confirmRow}
        onClose={() => setConfirmRow(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { direction } }}
      >
        <DialogTitle>{text.confirmGenerate.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {text.confirmGenerate.message}
          </DialogContentText>
          <DialogContentText sx={{ fontWeight: 700, color: "text.primary" }}>
            {confirmRow?.hebrewTitle || confirmRow?.originalTitle}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)} color="inherit">
            {text.confirmGenerate.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const row = confirmRow;
              setConfirmRow(null);
              handleGenerate(row);
            }}
            sx={{
              bgcolor: "text.primary",
              color: "background.default",
              "&:hover": { bgcolor: "text.secondary" },
            }}
          >
            {text.confirmGenerate.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function StoryCardView({
  item,
  index,
  mode,
  dir,
  text,
  writing,
  mine,
  showTopic,
  flash,
  cardRef,
  onWrite,
}) {
  const written = !!item.generatedArticleId;
  const score = typeof item.score === "number" ? item.score : null;
  const heat = heatColor(score ?? 5, mode);
  const tc = topicColor(item.topic, mode);
  const done = doneColor(mode);
  const fail = failColor(mode);
  // Someone else's finished story is context, not an offer — fade the story but
  // never the footer, which is still the way in to the draft.
  const muted = written && !mine;

  return (
    <StoryCard
      ref={cardRef}
      dir={dir}
      $flash={flash}
      style={{ animationDelay: `${Math.min(index, 11) * 22}ms` }}
    >
      {writing && (
        <LinearProgress
          sx={{
            position: "absolute",
            insetBlockStart: 0,
            insetInline: 0,
            height: 2,
          }}
        />
      )}
      <CardMeta dir="ltr">
        {score != null && (
          <Tooltip
            title={text.card.scoreTip.replace("{n}", score)}
            placement="top"
            arrow
          >
            <HeatBadge $bg={heat.bg} $fg={heat.fg}>
              {/* A grey flame is a cold fire — the icon only earns its place
                  where the score is actually warm. */}
              {score >= 6 && (
                <LocalFireDepartmentRoundedIcon sx={{ fontSize: 13 }} />
              )}
              {score}
            </HeatBadge>
          </Tooltip>
        )}
        <CountryTip code={item.country}>
          <Flag code={item.country} w={18} h={13} />
        </CountryTip>
        {/* On a topic tab the strip already says which desk this is. */}
        {showTopic && (
          <>
            <MetaDot>·</MetaDot>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: tc.fg }}
            >
              {text.topicNames[item.topic] || item.topic}
            </Typography>
          </>
        )}
        <MetaDot>·</MetaDot>
        <Typography variant="caption" color="text.secondary">
          {formatRelative(item.publishedAt, text.relative)}
        </Typography>
      </CardMeta>

      <CardTitle $muted={muted}>
        {item.hebrewTitle || item.originalTitle}
      </CardTitle>
      <CardDesc $muted={muted}>
        {item.hebrewDescription || item.originalDescription || ""}
      </CardDesc>

      <CardFoot>
        <SourceLink
          href={item.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={text.card.openSource}
        >
          {/* Items scanned before the publisher fix have no source name. */}
          {item.sourceName || text.card.openSource}
          <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
        </SourceLink>

        <CardAction>
          {written ? (
            <>
              <StatePill $bg={done.bg} $fg={done.fg}>
                <CheckCircleRoundedIcon sx={{ fontSize: 13 }} />
                {mine ? text.card.written : text.card.taken}
              </StatePill>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/editor/${item.generatedArticleId}`)}
              >
                {text.card.open}
              </Button>
            </>
          ) : writing ? (
            // Spinner alone: a labelled button here needs a startIcon, and MUI
            // takes that margin from the theme direction while the card carries
            // its own dir — so the icon landed on the wrong side of the text.
            // The height matches the button it replaces so the footer holds.
            <Tooltip title={text.card.writingHint} placement="top" arrow>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 30,
                  px: 1.5,
                }}
                aria-label={text.card.writing}
              >
                <CircularProgress size={18} />
              </Box>
            </Tooltip>
          ) : (
            <>
              {/* A dead job leaves the card actionable again — without this it
                  looks untried, and the next editor walks into the same wall. */}
              {item.lastFailedAt && (
                <Tooltip
                  title={item.lastFailedReason || text.card.failedHint}
                  placement="top"
                  arrow
                >
                  <StatePill $bg={fail.bg} $fg={fail.fg}>
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} />
                    {text.card.failed}
                  </StatePill>
                </Tooltip>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={onWrite}
                startIcon={<AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: "text.primary",
                  color: "background.default",
                  "&:hover": { bgcolor: "text.secondary" },
                }}
              >
                {item.lastFailedAt ? text.card.retry : text.card.write}
              </Button>
            </>
          )}
        </CardAction>
      </CardFoot>
    </StoryCard>
  );
}

// 50 countries is a menu's job; 8 topics is the strip's.
function FilterButton({ area, Icon, label, count, disabled, onClick }) {
  return (
    <Button
      variant="outlined"
      color="inherit"
      size="medium"
      disabled={disabled && count === 0}
      onClick={onClick}
      sx={{
        gridArea: area,
        width: "100%",
        textTransform: "none",
        borderColor: "divider",
        color: "text.primary",
        fontWeight: 500,
        px: 1.5,
        "&:hover": { borderColor: "text.secondary" },
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
      >
        <Badge
          color="primary"
          variant="dot"
          invisible={count === 0}
          overlap="circular"
        >
          <Icon fontSize="small" />
        </Badge>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {count > 0 && (
          <Typography variant="body2" color="text.secondary">
            · {count}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <ArrowDropDownIcon fontSize="small" />
      </Box>
    </Button>
  );
}

function CountryMenu({
  anchorEl,
  onClose,
  options,
  selected,
  onToggle,
  onClear,
  emptyLabel,
  clearLabel,
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            minWidth: 240,
            maxHeight: 380,
            borderRadius: 1.5,
            boxShadow: 6,
          },
        },
      }}
      MenuListProps={{ dense: true, sx: { py: 0.5 } }}
    >
      {options.length === 0 ? (
        <MenuItem disabled>
          <ListItemText
            primary={emptyLabel}
            primaryTypographyProps={{
              variant: "body2",
              color: "text.secondary",
            }}
          />
        </MenuItem>
      ) : (
        options.map((value) => {
          const active = selected.includes(value);
          return (
            <MenuItem
              key={value}
              onClick={() => onToggle(value)}
              sx={{
                py: 0.25,
                px: 1,
                gap: 0.5,
                borderRadius: 1,
                mx: 0.5,
                my: 0.25,
              }}
            >
              <Checkbox
                checked={active}
                size="small"
                disableRipple
                sx={{ p: 0.5 }}
              />
              <ListItemText
                primary={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Flag code={value} />
                    {value}
                  </Box>
                }
                primaryTypographyProps={{
                  variant: "body2",
                  noWrap: true,
                  fontWeight: active ? 600 : 400,
                }}
              />
            </MenuItem>
          );
        })
      )}
      {selected.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={onClear}
            sx={{ py: 0.75, justifyContent: "center" }}
          >
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontWeight: 500 }}
            >
              {clearLabel}
            </Typography>
          </MenuItem>
        </>
      )}
    </Menu>
  );
}

function nextScanLine(status, text) {
  if (!status?.nextRunAt) return text.status.scheduler.disabled;
  const ms = new Date(status.nextRunAt).getTime() - Date.now();
  if (ms <= 0) return text.status.scanning;
  const min = Math.max(1, Math.round(ms / 60000));
  return text.table.nextScanIn.replace("{n}", min);
}
